import { Switch } from './ui/switch';
import { Card } from './ui/card';
import { Zap, Wifi, Battery, Smartphone } from 'lucide-react';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';

export function SettingsPanel() {
  const [settings, setSettings] = useState({
    autoBoost: false,
    batteryOptimization: true,
    networkOptimization: true,
    cacheClearing: true,
  });

  // Load settings from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('fpsBoosterSettings');
    if (saved) {
      setSettings(JSON.parse(saved));
    }
  }, []);
  const toggleSetting = (key: keyof typeof settings) => {
    setSettings(prev => {
      const updated = { ...prev, [key]: !prev[key] };
      localStorage.setItem('fpsBoosterSettings', JSON.stringify(updated));

      // Show toast feedback
      const names: Record<string, string> = {
        autoBoost: 'Auto Boost',
        batteryOptimization: 'Battery Optimization',
        networkOptimization: 'Network Optimization',
        cacheClearing: 'Auto Cache Clear',
      };

      toast.success(`${names[key]} ${updated[key] ? 'enabled' : 'disabled'}`, {
        description: updated[key] ? '✓ Setting activated' : '✗ Setting deactivated',
      });

      return updated;
    });
  };

  const settingsList = [
    {
      id: 'autoBoost',
      label: 'Auto Boost',
      description: 'Automatically boost FPS when in games',
      icon: Zap,
    },
    {
      id: 'batteryOptimization',
      label: 'Battery Optimization',
      description: 'Reduce power consumption',
      icon: Battery,
    },
    {
      id: 'networkOptimization',
      label: 'Network Optimization',
      description: 'Optimize network latency',
      icon: Wifi,
    },
    {
      id: 'cacheClearing',
      label: 'Auto Cache Clear',
      description: 'Clear cache every 30 minutes',
      icon: Smartphone,
    },
  ];

  return (
    <div className="space-y-3">
      {settingsList.map(setting => {
        const Icon = setting.icon;
        const isEnabled = settings[setting.id as keyof typeof settings];
        return (
          <Card key={setting.id} className="p-4 backdrop-blur-sm border-primary/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                <div className="p-2 rounded-lg bg-secondary">
                  <Icon className={`${isEnabled ? 'text-accent' : 'text-muted-foreground'}`} size={18} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground text-sm">{setting.label}</h3>
                  <p className="text-xs text-muted-foreground">{setting.description}</p>
                </div>
              </div>
              <Switch
                checked={isEnabled}
                onCheckedChange={() => toggleSetting(setting.id as keyof typeof settings)}
              />
            </div>
          </Card>
        );
      })}
    </div>
  );
}